import React, { Component } from 'react';
import { connect } from "react-redux";
import actions from 'store/actions';
import { LoadingOverlay, ProhibitedArea } from "components";
import BusinessAreaLoadError from "components/molecules/BusinessAreaLoadError";
import { isEntityId, loadFailureKind, replacePathSegment } from "common/businessAreaRoute";
import AppPaths from "constants/appPaths";
import { withRouter } from "react-router";

export class UserBusinessAreaSchemaWrapperComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            fetchingUserBusinessArea: false,
            fetchingSchemaModel: false,
            fetchingSchemaModelPermission: false,
            busy: false,
            busyMessage: "Please wait...",
            // Why the business area could not be loaded: "notFound", "denied" or "failed".
            businessAreaFailure: null,
        };
    }

    componentWillMount() {
        this.loadComponent();
    }

    componentDidMount() {
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        // Another business area or schema in the URL (a link resolved from ids moves to names).
        if (prevProps.businessArea !== this.props.businessArea || prevProps.schema !== this.props.schema) {
            this.retry();
            return;
        }
        this.onGetUserBusinessAreaByNameResultChanged(prevProps);
        this.onGetUserSchemaModelsPermissionResultChanged(prevProps);
        this.onGetSchemaModelResultChanged(prevProps);
    }

    componentWillUnmount() {
    }

    loadComponent = () => {
        if (!this.props.userBusinessAreas[this.props.businessArea]) {
            this.setState({
                fetchingUserBusinessArea: true,
                busyMessage: "Please wait",
                businessAreaFailure: null
            });
            this.props.getUserBusinessAreaByName(this.props.businessArea);
        }
        else {
            this.setSchemaPermissions(this.props.schemaPermission, this.props.userBusinessAreas[this.props.businessArea]);
            this.setState({
                fetchingUserBusinessArea: false,
                businessAreaFailure: null
            });
            if (!this.props.schemaModel ||
                this.props.schemaModel.name.toLowerCase() !== this.props.schema.toLowerCase() ||
                this.props.schemaModel.businessAreaId !== this.props.userBusinessAreas[this.props.businessArea].businessAreaId) {
                this.setState({
                    fetchingSchemaModel: true
                });
                this.props.getSchemaModel(this.props.userBusinessAreas[this.props.businessArea].businessAreaId, this.props.schema.toLowerCase());
            }
        }

        if (!this.props.schemaPermission) {
            this.setState({
                fetchingSchemaModelPermission: true,
                busyMessage: "Please wait...",
            });
            this.props.getUserSchemasPermission();
        }
        else {
            this.setState({
                fetchingSchemaModelPermission: false,
            });
            this.setSchemaPermissions(this.props.schemaPermission, this.props.userBusinessAreas[this.props.businessArea]);
        }
    }

    onGetUserBusinessAreaByNameResultChanged = (prevProps) => {
        if (this.props.getUserBusinessAreaByNameResult && this.props.getUserBusinessAreaByNameResult !== prevProps.getUserBusinessAreaByNameResult) {
            if (!this.props.getUserBusinessAreaByNameResult.success) {
                this.setState({
                    businessAreaFailure: loadFailureKind(this.props.getUserBusinessAreaByNameResult)
                });
            }
            else if (!this.moveToNameIfResolvedFromId()) {
                this.loadComponent();
            }
            this.setState({
                fetchingUserBusinessArea: false
            })
        }
    }

    onGetUserSchemaModelsPermissionResultChanged = (prevProps) => {
        if (this.props.getUserSchemaModelsPermissionResult && this.props.getUserSchemaModelsPermissionResult !== prevProps.getUserSchemaModelsPermissionResult) {
            if (!this.props.getUserSchemaModelsPermissionResult.success) {
                this.setState({
                    permissionDenied: true
                });
            }
            else {
                this.setSchemaPermissions(this.props.schemaPermission, this.props.userBusinessAreas[this.props.businessArea]);
            }
            this.setState({
                fetchingSchemaModelPermission: false
            });
        }
    }

    onGetSchemaModelResultChanged = (prevProps) => {
        if (this.props.getSchemaModelResult && this.props.getSchemaModelResult !== prevProps.getSchemaModelResult) {
            if (!this.props.getSchemaModelResult.success) {
                if (this.props.getSchemaModelResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        permissionDenied: true
                    });
                }
            }
            else {
                this.moveSchemaToNameIfResolvedFromId();
            }
            this.setState({
                fetchingSchemaModel: false
            });
        }
    }

    // The business area was asked for by its id (a link built from ids): the page moves to its
    // name, as every other link has it. Whether it moved.
    moveToNameIfResolvedFromId = () => {
        const { requestedName } = this.props.getUserBusinessAreaByNameResult;
        const businessArea = Object.values(this.props.userBusinessAreas || {}).find((item) => item);
        if (!requestedName || requestedName !== this.props.businessArea || !isEntityId(requestedName) || !businessArea) {
            return false;
        }
        this.moveTo(replacePathSegment(this.props.location.pathname, "businessareas", requestedName, businessArea.name.toLowerCase()));
        return true;
    }

    moveSchemaToNameIfResolvedFromId = () => {
        const { schemaModel, schema } = this.props;
        if (schemaModel && isEntityId(schema) && schemaModel.schemaId === schema && schemaModel.name) {
            this.moveTo(replacePathSegment(this.props.location.pathname, "schemas", schema, schemaModel.name));
        }
    }

    moveTo = (pathname) => {
        if (pathname !== this.props.location.pathname) {
            this.props.history.replace({ ...this.props.location, pathname });
        }
    }

    // Loads the business area and schema again (after a failure, or for another URL).
    retry = () => {
        this.setState({
            businessAreaFailure: null,
            permissionDenied: false,
        }, this.loadComponent);
    }

    setSchemaPermissions = (permission, userBusinessArea) => {
        let schemaPermission = { canAdd: false, canView: false, canEdit: false, canDelete: false };
        if (permission && userBusinessArea && userBusinessArea.user) {
            schemaPermission = {
                canAdd: permission.canAdd && userBusinessArea.user.canAdd,
                canView: permission.canView && userBusinessArea.user.canView,
                canEdit: permission.canEdit && userBusinessArea.user.canEdit,
                canDelete: permission.canDelete && userBusinessArea.user.canDelete,
            };
        }
        this.setState({
            schemaPermission
        });
    }

    // Why the schema could not be shown: "notFound" or "failed" (or null when it is there).
    getSchemaFailure = () => {
        if (!this.props.schemaModel) {
            return this.props.getSchemaModelResult.success ? "notFound" : "failed";
        }
        return null;
    }

    isBusy = () => {
        let isBusy = this.state.busy || this.state.fetchingUserBusinessArea || this.state.fetchingSchemaModelPermission || this.state.fetchingSchemaModel;
        return isBusy;
    }

    render() {
        if (this.state.permissionDenied) {
            return <ProhibitedArea></ProhibitedArea>;
        }
        if (this.state.businessAreaFailure) {
            return (
                <BusinessAreaLoadError
                    kind={this.state.businessAreaFailure}
                    name={this.props.businessArea}
                    listPath={AppPaths.TENANT_HOME.replace(":tenant", this.props.tenant.toLowerCase())}
                    onRetry={this.retry}
                />
            );
        }
        let schemaFailure = this.getSchemaFailure();
        return this.isBusy() ?
            <div className='page-container'>
                <LoadingOverlay
                    busy={this.isBusy()}
                    spinner
                    message={this.state.busyMessage || "Please wait..."}>
                </LoadingOverlay>
            </div>
            :
            (
                !schemaFailure ?
                    this.props.children({ "userBusinessArea": this.props.userBusinessAreas[this.props.businessArea] })
                    :
                    <BusinessAreaLoadError
                        kind={schemaFailure}
                        subject="schema"
                        name={this.props.schema}
                        listPath={AppPaths.TENANT_BUSINESS_AREA_SCHEMA.replace(":tenant", this.props.tenant.toLowerCase()).replace(":businessarea", this.props.businessArea)}
                        onRetry={this.retry}
                    />
            )
    }
}

const mapStateToProps = (state) => {
    return {
        userBusinessAreas: state.schemaModels.userBusinessAreas,
        getUserBusinessAreaByNameResult: state.schemaModels.getUserBusinessAreaByNameResult,
        getSchemaModelResult: state.schemaModels.getSchemaModelResult,
        schemaModel: state.schemaModels.schemaModel,
        getUserSchemaModelsPermissionResult: state.schemaModels.getUserSchemaModelsPermissionResult,
        schemaPermission: state.schemaModels.permission,
    };
}

const mapDispatchToProps = (dispatch) => {
    return {
        getUserBusinessAreaByName: (businessAreaName) => dispatch(actions.getUserBusinessAreaByNameRequest(businessAreaName)),
        getSchemaModel: (businessAreaId, schemaName) => dispatch(actions.getSchemaModelByNameRequest(businessAreaId, schemaName)),
        getUserSchemasPermission: () => dispatch(actions.getUserSchemaModelsPermissionRequest()),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(UserBusinessAreaSchemaWrapperComponent));